// SDK + session-key promises cached at module level; per-caller
// createUspInstance so pages don't share context. 5s timeout on loader
// readiness so an ad-blocked script can't hang start() forever.

import getEnvVariables from "lib/environmentVariables/getEnvVariables";
import {
  UspContext,
  UspCreateContextConfig,
  UspInstance,
  UspSdk,
} from "./types";

const LOADER_PATH = "/assets/loader.min.js";
const READY_TIMEOUT_MS = 5000;

type UspConfig = {
  clientId: string;
  host: string;
  pixelHost: string;
  environment: "sandbox" | "production";
};

const readConfig = (): UspConfig | null => {
  const env = getEnvVariables();
  if (!env.uspClientId || !env.uspHost) {
    return null;
  }
  return {
    clientId: env.uspClientId,
    host: env.uspHost,
    pixelHost: env.uspPixelHost || env.uspHost,
    environment: env.uspEnvironment,
  };
};

type Promises = {
  sdk: Promise<UspSdk | null> | null;
  sessionKey: Promise<string | null> | null;
};

const promises: Promises = { sdk: null, sessionKey: null };

const injectLoader = (host: string): Promise<UspSdk | null> =>
  new Promise((resolve) => {
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://${host}${LOADER_PATH}`;
    const timeoutId = setTimeout(() => resolve(null), READY_TIMEOUT_MS);
    script.onload = () => {
      clearTimeout(timeoutId);
      resolve(window._Sardine ?? null);
    };
    script.onerror = () => {
      clearTimeout(timeoutId);
      resolve(null);
    };
    document.head.appendChild(script);
  });

// Only the UUID portion goes to the SDK; the signed `<uuid>.<sig>` form
// stays server-side.
const stripSignature = (signed: string): string => {
  const dot = signed.indexOf(".");
  return dot > 0 ? signed.slice(0, dot) : signed;
};

const fetchSessionKey = async (): Promise<string | null> => {
  const env = getEnvVariables();
  try {
    const response = await fetch(`${env.idApiUrl}/usp/session-init`, {
      credentials: "include",
    });
    if (response.status === 204 || !response.ok) {
      return null;
    }
    const body: { session_key?: string } = await response.json();
    if (!body.session_key) {
      return null;
    }
    return stripSignature(body.session_key);
  } catch {
    return null;
  }
};

const getSdk = (): Promise<UspSdk | null> => {
  if (!promises.sdk) {
    const config = readConfig();
    if (!config) return Promise.resolve(null);
    promises.sdk = injectLoader(config.host);
  }
  return promises.sdk;
};

const getSessionKey = (): Promise<string | null> => {
  if (!promises.sessionKey) {
    promises.sessionKey = fetchSessionKey();
  }
  return promises.sessionKey;
};

// Called at bootstrap so useUspFlow doesn't wait on the loader script.
export const preloadUspSdk = (): void => {
  void getSdk();
};

export const createUspInstance = (): UspInstance => {
  let context: UspContext | null = null;
  let stopped = false;

  const start = async (flow: string): Promise<void> => {
    const config = readConfig();
    if (!config || stopped) {
      return;
    }

    const [sdk, sessionKey] = await Promise.all([getSdk(), getSessionKey()]);
    if (!sdk || !sessionKey || stopped) {
      return;
    }

    // Tear down any previous context on this instance before creating a new
    // one, so bind() isn't called twice on the same listeners.
    if (context) {
      try {
        context.unbind();
      } catch {
        // SDK may be in a bad state; swallow.
      }
      context = null;
    }

    const createConfig: UspCreateContextConfig = {
      clientId: config.clientId,
      sessionKey,
      flow,
      environment: config.environment,
      apiSubdomain: config.host,
      pixelSubdomain: config.pixelHost,
      ...(document.body ? { parentElement: document.body } : {}),
    };

    const next = sdk.createContext(createConfig);
    if (stopped) {
      next.unbind();
      return;
    }
    context = next;
  };

  const stop = (): void => {
    stopped = true;
    if (context) {
      try {
        context.unbind();
      } catch {
        // SDK may be in a bad state; swallow.
      }
      context = null;
    }
  };

  return { start, stop };
};
